import { Component, ChangeDetectionStrategy, ViewEncapsulation, computed, inject, Injector, viewChild, effect, TemplateRef, ElementRef, afterNextRender, afterEveryRender, DestroyRef, signal } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Command } from '@workbuddy/ui/model';
import { ButtonComponent, ButtonDisplayStyle, ButtonDropdownComponent, ButtonIconComponent, ButtonMenuComponent, ButtonOverlayComponent, ButtonOverlayContentDirective, ButtonSplitComponent } from '@workbuddy/ui/v2/components/button';
import { SearchComponent } from '@workbuddy/ui/fields/search';
import { Paginator, PaginatorModule, PaginatorState } from 'primeng/paginator';
import { BadgeComponent, BadgeMode } from '@workbuddy/ui/components/badge';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';
import { ActivityEntityName, EntityViewDisplay } from '@jms/model';
import { EntityManifestService } from '@workbuddy/services/entities';
import { applyAddBlockedReason, canFavoriteBoardView, type EntityBoardAddCommandData, isAddCommandsMenu, isViewSelectorEnabled, isViewSelectorPills, resolveBoardAddInitialValues, resolveBoardCreateParams, resolveHeaderCommandList, resolveHeaderPrimaryCommandList, resolveViewOptions } from '../../context';
import { resolveBoardCreatePresentation } from '../../model/entity.board.create.presentation';
import { EntityBoardDensityState, EntityBoardStore } from '../../state';
import { FilterBarComponent, FilterFieldProvider, FilterPeriodComponent, FILTER_ADVANCED_FILTER_ID, type FilterPeriodPreset, type FilterPeriodRange } from '@workbuddy/ui/v2/blocks/filter';
import { DisplaySettingsComponent, DisplaySettingsValue } from './display.settings.component';
import { BoardViewPillsComponent } from '../board.view.pills';
import { BoardHeaderNavigationComponent } from './board.header.navigation.component';
import { PAGE_COMPONENTS } from '@workbuddy/ui/blocks/page';
import { BoardHeaderCommandIds } from './model';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { boardHeaderBridgeStyles, boardHeaderStyleId } from './board.header.theme';
// Deep path, not the barrel: entity.dialog's host mounts the inspector renderer, which can mount a
// relation board — the barrel would close a cycle. The contracts file imports nothing back.
import { EntityDialogs } from '@workbuddy/ui/v2/blocks/entity.dialog/entity.dialog.contracts';
import { ENTITY_BOARD_DIALOG_OUTLET } from '../../entity.board.dialog';
import { revealCreatedBoardRecord } from './reveal.created.board.record';

@Component({
    selector: 'board-header',
    templateUrl: 'board.header.component.html',
    styleUrls: ['board.header.component.less', 'board.header.view.selector.less'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    host: {
        '[class.is-compact]': '$compact()',
        '[class.is-narrow]': '$narrow()',
        '[class.is-single-row]': 'store.$context()?.chrome === "flush"',
        '[class.segments-stacked]': '$segmentsStacked()',
        '[class.has-header-navigation]': '$showCalendarNavigation()',
        '[class.navigation-stacked]': '$navigationStacked()',
    },
    imports: [
        CommonModule,
        PaginatorModule,
        ButtonComponent,
        ButtonDropdownComponent,
        ButtonIconComponent,
        ButtonMenuComponent,
        ButtonOverlayComponent,
        ButtonOverlayContentDirective,
        ButtonSplitComponent,
        SearchComponent,
        FilterBarComponent,
        BadgeComponent,
        DisplaySettingsComponent,
        BoardViewPillsComponent,
        BoardHeaderNavigationComponent,
        FilterPeriodComponent,
        PAGE_COMPONENTS,
    ],
})
export class BoardHeaderComponent {
    protected readonly ButtonDisplayStyle = ButtonDisplayStyle;

    protected store = inject(EntityBoardStore);
    /**
     * Period chrome, rendered beside the view switcher when the board declares a period.
     *
     * The STORE's computed, never the raw context: a period declared on the entity manifest
     * (`board.defaults.period` — Visit, Upcoming Work) reaches the context as undefined, so reading
     * the context here drew no pill on exactly the boards whose reads the store was already
     * bounding. Optional-called so a partial store double still renders a period-less header.
     */
    protected readonly $periodConfig = computed(() => this.store.$periodConfig?.() ?? null);
    // A server-grouped view reads a backlog no period bounds, so the pill would steer nothing.
    protected readonly $periodRange = computed(() => (this.$periodConfig() && !this.store.$serverGroups?.() ? this.store.$periodRange?.() ?? null : null));
    protected readonly $periodRangeLabel = computed(() => this.store.$periodRangeLabel?.() ?? '');
    protected readonly $periodOptions = computed(() => this.store.$periodOptions?.() ?? []);
    protected readonly $periodNextBlocked = computed(() => this.store.$periodNextBlocked?.() ?? false);
    protected readonly $periodMaxDate = computed(() => this.store.$periodMaxDate?.() ?? null);
    /**
     * One navigation per header, and on a period-declared board it is the PILL.
     *
     * A Calendar brings its own `‹ Today ›` and month-title dropdown. Beside a period pill that is
     * two controls offering the same choice through different chrome, drifting apart the moment
     * either moves. The pill wins because it is the one control that means the same thing on every
     * display: the same vocabulary, the same arrows, the same label, whether the board is drawing a
     * grid or a table. The calendar toolbar is therefore suppressed while a period is declared, and
     * the pill drives the grid instead. Boards with no period keep the toolbar exactly as before.
     */
    protected readonly $showCalendarNavigation = computed(() => !this.$periodConfig() && !!this.store.$headerNavigation());
    /**
     * The custom range is a Table-only affordance.
     *
     * A grid lays out whole months and whole weeks; it cannot draw "3 Sep to 19 Nov", so offering a
     * hand-drawn range on Calendar would promise a window the display cannot honour.
     */
    protected readonly $periodCustom = computed(() => !!this.$periodConfig()?.custom && this.store.$effectiveDisplay() !== EntityViewDisplay.Calendar);
    private readonly defaultAddTooltip = this.store.addCommand.tooltip;
    protected readonly density = inject(EntityBoardDensityState);
    protected readonly $compact = this.density.$compact;
    protected readonly $narrow = this.density.$narrow;

    /**
     * PrimeNG breaks when first > 0 and totalRecords is still 0 (restored page before total returns):
     * page links vanish and only arrows remain. Keep first at 0 until we have a real total; the store
     * page (and the items request) stay on the restored value.
     */
    protected readonly $paginatorFirst = computed(() => {

        const total = this.store.$total();
        const page = this.store.$page();
        const rows = this.store.$effectivePageSize();
        if (!total || total <= 0) return 0;
        return page * rows;
    });

    /**
     * What the pager's left-hand slot says.
     *
     * A pending board has no total, so the range would read "Showing 0 - 0 of 0" — a claim about data
     * the server has not returned, the same lie `board-empty` was stopped from telling. It reports
     * the wait instead. Deliberately the same SLOT rather than a hidden one: an empty report collapses
     * the row and re-centres the arrows, so the pager visibly jumps when the count lands.
     *
     * A reload that already knows its total keeps the range — a page flip must not blink the count out.
     */
    protected readonly $pageReportTemplate = computed(() => {

        const pending = this.store.$itemsLoading() && (this.store.$total() ?? 0) <= 0;
        // A server-grouped page counts groups, so a bare number would read as rows.
        const unit = this.store.$serverGroups?.()?.unit;
        if (pending) return 'Loading…';
        return unit ? `Showing {first} - {last} of {totalRecords} ${unit}` : 'Showing {first} - {last} of {totalRecords}';
    });
    protected BadgeMode = BadgeMode;
    protected ActivityEntityName = ActivityEntityName;
    private filterProvider = inject(FilterFieldProvider);
    private entityManifests = inject(EntityManifestService);
    private document = inject(DOCUMENT);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    // Optional: a board can be rendered outside the shell (a portal, a standalone page) where no dialog
    // host is provided. A context-free create can then fall back to the board's own outlet.
    private readonly dialogs = inject(EntityDialogs, { optional: true });
    private readonly injector = inject(Injector);

    protected $viewOptions = computed(() => resolveViewOptions(this.store.$context()?.header?.showViewOptions, this.store.$contextIsEmbedded()));

    /** Pills replace the view dropdown when `header.showViewOptions.selector` is `'pills'`. */
    protected readonly $showViewPills = computed(() => isViewSelectorPills(this.$viewOptions().selector) && this.store.$viewCommands().length > 0);

    protected readonly $showViewDropdown = computed(() => isViewSelectorEnabled(this.$viewOptions().selector) && !isViewSelectorPills(this.$viewOptions().selector) && this.store.$viewCommands().length > 1);

    protected readonly $viewSelectorCommands = computed(() => this.store.$viewCommands().map(command => command.clone({ tooltip: command.label })));

    protected readonly $singleView = computed(() => {
      if (!isViewSelectorEnabled(this.$viewOptions().selector) || isViewSelectorPills(this.$viewOptions().selector)) return null;
      const commands = this.store.$viewCommands();
      return commands.length === 1 ? commands[0] : null;
    });

    protected readonly $showViewFavorite = computed(() => canFavoriteBoardView(this.$viewOptions().favorite, this.store.$viewCommands().length) && !!this.store.$view());

    protected readonly $showHeaderTitle = computed(() => {
      const header = this.store.$context()?.header;
      if (!header?.title) return false;
      return !this.store.$contextIsEmbedded() || header.showTitle === true;
    });

    protected readonly $showBoardControls = computed(() => this.store.$context()?.header?.showBoardControls !== false);

    // Header chrome: static arrays, signals, or inject-capable factories.
    // primaryCommands may mix plain buttons and splits (see resolveHeaderPrimaryCommandList).
    protected $primaryCommands = computed(() => resolveHeaderPrimaryCommandList(this.store.$context()?.header?.primaryCommands, this.injector));
    protected $moreCommands = computed(() => resolveHeaderCommandList(this.store.$context()?.header?.moreCommands, this.injector));
    protected $addCommands = computed(() => resolveHeaderCommandList(this.store.$context()?.header?.addCommands, this.injector).map(command => this.bindAddCommand(command)));
    /** Questions: + opens the type list. Invoices keep the default-create split. */
    protected readonly $addAsMenu = computed(() => isAddCommandsMenu(this.store.$context()?.header?.addMenu));

    // Only the preview panel relocates the paginator into the board's split footer; a quickview has
    // no footer of its own, so the header keeps rendering it.
    protected $previewPanelOpen = computed(() => this.store.$panelOpen() && this.store.$panelKind() === 'preview');

    // Preview only: it is closed explicitly and would otherwise be a one-way door until the context
    // changes. A quickview is reopened by selecting a row, so it needs no control here.
    protected $panelReopenable = computed(() => this.store.$panelKind() === 'preview' && this.store.$panelClosed());

    // Board types offered by the quick display switcher — manifest-declared (e.g. financial docs =
    // Card / Table). Null falls through to Table only; other layouts are an explicit opt-in.
    protected $displayTypes = computed(() => {

        const context = this.store.$context();
        const restricted = context?.restrictions?.supportedDisplays;
        if (restricted?.length) return [...restricted];
        const entity = context?.entity;
        return entity ? this.entityManifests.board(entity, context).supportedDisplays ?? null : null;
    });

    /** Promotion writes the shared view, so it is offered only where the view itself is editable. */
    protected readonly $canEditView = computed(() => this.store.$view()?.canEdit === true);

    /** The shared default, so the panel can tell a personal layout from the view's own. */
    protected readonly $viewDisplay = computed(() => this.store.$viewDeclaredDisplay());

    /**
     * Header "Filter" menu — same exclusion set as the filter-bar `+` (quick filters, blocked,
     * already-added user chips). Built from the bar when mounted so Assignee etc. never double-list.
     */
    protected $filterCommands = computed(() => {

        const bar = this.filterBar();
        if (bar) return bar.$addMenuCommands();

        // Pre-viewChild fallback (bar not mounted yet): still drop quick/hidden ids.
        const used = new Set<string>([...(this.store.$quickFilterFieldIds() ?? []), ...(this.store.$hiddenFilterFieldIds() ?? [])]);
        const fields = this.filterProvider.getFields()()
            .filter(f => !!f?.id && !used.has(f.id))
            .map(f => new Command({ _id: f.id, label: f.label, iconLeft: f.icon ? `fa-regular ${f.icon}` : null, data: f }));

        if (this.store.$allowAdvancedQuery() && !used.has(FILTER_ADVANCED_FILTER_ID)) {
            fields.push(new Command({ _id: 'divider', divider: true }));
            fields.push(new Command({ _id: FILTER_ADVANCED_FILTER_ID, label: 'Advanced Filter', iconLeft: 'fa-regular fa-filter-list' }));
        }

        return fields;
    });

    private ignorePageChange: boolean;

    readonly paginator = viewChild<Paginator>('pager');
    readonly paginatorTemplate = viewChild<TemplateRef<unknown>>('paginatorTpl');
    /** True when pills cannot sit on the chrome row without clipping — whole group goes below. */
    protected readonly $segmentsStacked = signal(false);
    protected readonly $navigationStacked = signal(false);

    private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
    private segmentLayoutRo?: ResizeObserver;
    private navigationLayoutRo?: ResizeObserver;
    private navigationRow: HTMLElement | null = null;

    protected onPeriodPreset(preset: FilterPeriodPreset): void {

        this.store.setPeriodPreset(preset);
        this.restartPaging();
    }

    protected onPeriodCustom(range: FilterPeriodRange): void {

        this.store.setPeriodCustom(range.from, range.to);
        this.restartPaging();
    }

    protected onPeriodStep(direction: -1 | 1): void {

        this.store.stepPeriod(direction);
        this.restartPaging();
    }

    /**
     * A new period is a new result set, exactly like a new filter: the page index from the old one
     * is meaningless in it. Left alone, a viewer on page 2 of a month who steps to a week asks for a
     * second page the week does not have and sees an empty board.
     *
     * A keyset walk's cursors and snapshot token belong to the OLD result set — resuming the new one
     * from a position taken out of rows it no longer contains — so they go with the page index.
     */
    private restartPaging(): void {

        this.store.clearSelectedItems();
        this.store.updateHeader({ page: 0, cursors: [undefined], readToken: undefined });
        this.resetPaginator();
    }

    constructor() {

        DesignSystem.Mount(this.document, boardHeaderStyleId, boardHeaderBridgeStyles);

        inject(DestroyRef).onDestroy(() => {
            this.segmentLayoutRo?.disconnect();
            this.navigationLayoutRo?.disconnect();
        });
        afterNextRender(() => this.bindSegmentLayout());
        afterEveryRender(() => {
            this.updateNavigationLayout();
            this.updateSegmentLayout();
        });

        // Sync Add visibility: explicit header.hideAdd, else authorize create when the entity declares it.
        // addBlockedReason keeps + visible but aria-disabled with that tooltip.
        effect(() => {
            const context = this.store.$context();
            if (!context) return;

            applyAddBlockedReason(this.store.addCommand, context.header?.addBlockedReason, this.defaultAddTooltip);

            if (context.header?.hideAdd || this.store.$ganttWritable?.() === false) {
                this.store.addCommand.hidden = true;
                return;
            }

            const entity = context.entity;
            if (!entity || !this.entityManifests.supportsOperation(entity, 'create')) {
                // No manifest create op — leave visible for legacy board-outlet hosts that still use Add.
                this.store.addCommand.hidden = false;
                return;
            }

            // Async rights check; hide until allowed so denied users never see a dead Add control.
            this.store.addCommand.hidden = true;
            void this.entityManifests.authorizeOperation(entity, 'create', undefined, this.injector).then(allowed => {
                // Context may have changed while the promise was in flight.
                if (this.store.$context()?.entity !== entity) return;
                if (this.store.$context()?.header?.hideAdd || this.store.$ganttWritable?.() === false) {
                    this.store.addCommand.hidden = true;
                    return;
                }
                this.store.addCommand.hidden = !allowed;
            });
        });


        this.store.commandActions$
            .pipe(
                takeUntilDestroyed(),
                tap(cmd => {
                    if (cmd._id === 'ViewHeaderRefresh') {
                        this.store.refresh();
                    }
                    if (cmd._id === BoardHeaderCommandIds.ViewFavorite) {
                        this.store.favoriteView();
                    }
                    if (cmd._id === BoardHeaderCommandIds.ViewEdit) {
                        this.store.setViewSettingsOpen(true, 'edit');
                    }
                    if (cmd._id === BoardHeaderCommandIds.ViewCreate) {
                        this.store.openCreateView();
                    }
                    if (cmd._id === BoardHeaderCommandIds.Add) {
                        if (this.store.$ganttWritable?.() === false) {
                            return;
                        }
                        // Parent-scoped create (job Quotes / Invoices / …) owns the + via onAdd.
                        const onAdd = this.store.$context()?.header?.onAdd;
                        if (onAdd) {
                            onAdd();
                            return;
                        }
                        // Open create in the shell dialog, falling back to the board's own outlet.
                        // Hosts can still listen via header.onCommand for side effects.
                        void this.openCreateDialog(cmd);
                    }
                    this.store.$context()?.header?.onCommand?.(cmd);
                })
            )
            .subscribe();
    }

    private bindSegmentLayout(): void {

        this.segmentLayoutRo?.disconnect();
        const row = this.headerChromeRow();
        if (!row) return;
        this.segmentLayoutRo = this.watchSize(row, () => this.updateSegmentLayout());
        this.updateSegmentLayout();
    }

    private headerChromeRow(): HTMLElement | null {

        return this.host.nativeElement.querySelector('.header-row-normal');
    }

    /** jsdom (and any non-browser host) has no ResizeObserver; still run a one-shot layout. */
    private watchSize(element: HTMLElement, onResize: () => void): ResizeObserver | undefined {

        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(onResize);
        observer.observe(element);

        return observer;
    }

    private updateNavigationLayout(): void {
        const row = this.host.nativeElement.querySelector<HTMLElement>('.board-header-page > .page-header');
        if (row !== this.navigationRow) {
            this.navigationLayoutRo?.disconnect();
            this.navigationRow = row;
            this.navigationLayoutRo = row ? this.watchSize(row, () => this.updateNavigationLayout()) : undefined;
        }
        if (!row || this.$compact()) {
            this.$navigationStacked.set(false);
            return;
        }
        const left = row.querySelector<HTMLElement>('.header-calendar-identity');
        const right = row.querySelector<HTMLElement>('.header-calendar-actions');
        const navigation = row.querySelector<HTMLElement>('board-header-navigation');
        if (!left || !right || !navigation || !row.clientWidth) return;
        const style = getComputedStyle(row);
        const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
        const gap = parseFloat(style.gap) || 16;
        const side = Math.max(left.getBoundingClientRect().width, right.getBoundingClientRect().width);
        const required = 2 * side + navigation.getBoundingClientRect().width + 2 * gap + padding;
        this.$navigationStacked.set(required > row.clientWidth - (this.$navigationStacked() ? 16 : 0));
    }

    private updateSegmentLayout(): void {

        const row = this.headerChromeRow();
        const slot = row?.querySelector<HTMLElement>('.header-after-views');
        const pills = slot?.querySelector<HTMLElement>('.segment-pills, .segment-tiles');
        if (!row || !slot || !pills) return;

        const gap = parseFloat(getComputedStyle(row).columnGap || getComputedStyle(row).gap) || 0;
        let chrome = 0;
        let chromeCount = 0;
        for (const child of Array.from(row.children) as HTMLElement[]) {
            if (child === slot || child.classList.contains('header-after-views')) continue;
            if (getComputedStyle(child).display === 'none') continue;
            // module-options is flex:1 spacer — only its buttons count, not the stretched box.
            const width = child.classList.contains('module-options')
                ? Array.from(child.children).reduce((sum, el) => sum + (el as HTMLElement).getBoundingClientRect().width, 0)
                : child.getBoundingClientRect().width;
            if (width <= 0) continue;
            chrome += width;
            chromeCount += 1;
        }

        const available = row.clientWidth - chrome - gap * (chromeCount + 1);
        const needed = pills.scrollWidth;
        const slack = this.$segmentsStacked() ? 32 : 0;
        const stacked = needed > available - slack;
        if (this.$segmentsStacked() !== stacked) this.$segmentsStacked.set(stacked);
    }

    /**
     * Opens entity create from the board Add control.
     *
     * Prefers the shell entity dialog (works for embedded boards). Forwards opaque board `params` and
     * optional command `initialValues` / `type`. Construction and presentation come from
     * `manifest.operations.create` (surface or custom). Mount `createTitle` replaces only the noun.
     *
     * Falls back to the board route outlet only for seedless, param-less creates when no shell dialog
     * is available. When the entity declares create (or host params are present), a failed shell open
     * must not fall through (that would look like success without rights or without host scope).
     */
    private async openCreateDialog(command?: Command): Promise<void> {

        const data = command?.data as EntityBoardAddCommandData | undefined;
        const type = data?.type;
        const context = this.store.$context();
        const entity = context?.entity;
        const params = resolveBoardCreateParams(context);
        const hasParams = params !== undefined;
        const initialValues = resolveBoardAddInitialValues(context?.header, data);

        if (entity && this.dialogs) {
            const opened = await this.dialogs.open({
                entity,
                operation: 'create',
                params,
                initialValues,
                presentation: resolveBoardCreatePresentation({
                  createTitle: context?.createTitle,
                  construction: this.entityManifests.resolveOperation(entity, 'create')?.presentation,
                }),
                source: { entity },
                afterSave: result => revealCreatedBoardRecord(this.store, entity, result),
            });
            if (opened || hasParams || this.entityManifests.supportsOperation(entity, 'create')) return;
        }

        // Host params cannot be represented on the board outlet — never fall through.
        if (hasParams) return;

        void this.router.navigate(
            [{ outlets: { [ENTITY_BOARD_DIALOG_OUTLET]: ['new'] } }],
            {
                relativeTo: this.route,
                queryParams: type ? { type } : {},
            },
        );
    }

    /**
     * Binds actionless Add-split commands (`initialValues` / `type` only) to {@link openCreateDialog}.
     * Commands that already define `action` stay imperative (e.g. non-create tools).
     */
    private bindAddCommand(command: Command): Command {

        const data = command.data as EntityBoardAddCommandData | undefined;
        if (command.action || (!data?.initialValues && !data?.type)) return command;

        const bound = command.clone();
        bound.action = () => void this.openCreateDialog(command);
        return bound;
    }

    protected onViewChanged(view: any) {

        const viewId = this.store.$viewId();
        if (viewId === view._id) return;

        this.store.clearSelectedItems();
        this.store.changeView(view);
        this.resetPaginator();
    }

    protected onResetDisplaySettings(): void {

        // Compact overlay footer: underline hover, no fill.
        this.store.clearOwnedViewDisplay();
        this.store.updateHeader({ page: 0, pageSize: null, sort: null });
        this.store.clearSelectedItems();
        this.resetPaginator();
        queueMicrotask(() => this.store.refresh());
    }

    /**
     * Promotes this user's layout to the view's shared default.
     *
     * The personal override is dropped in the same act: leaving it behind would mean the user who set
     * the default is the one person still reading a private copy of it, and any later change to the
     * view's layout would be invisible to them.
     */
    protected async onSetDefaultDisplay(display: EntityViewDisplay): Promise<void> {

        this.store.ownViewDisplay({ display });
        await this.store.saveView();
        this.store.clearOwnedViewDisplay();
    }

    protected onDisplaySettingsChange(value: DisplaySettingsValue): void {

        // Board owns display on the loaded view (not ephemeral previewView) so Table/Card +
        // showPreview / groupBy / list fields stick across refresh. Page-size still flows through header override.
        const pageSizeChanged = value.pageSize !== this.store.$effectivePageSize();
        const table = value.view?.table as { groupBy?: string | null; subGroupBy?: string | null } | undefined;
        const list = value.view?.list as { fields?: string[]; titleLines?: 1 | 2 | 3; showEmptyGroups?: boolean; showNestedItems?: boolean; indentNestedItems?: boolean } | undefined;
        const normalize = (g: string | null | undefined) => (g == null || g === '' || g === 'none' ? null : g);
        // The panel omits `table` when it shows no Group control (Kanban, or nothing groupable).
        // Absent means unchanged — reading it as None would wipe an owned groupBy on a page-size change.
        const groupByEmitted = table !== undefined;
        const prevGroupBy = normalize((this.store.$effectiveView()?.table as { groupBy?: string | null } | undefined)?.groupBy);
        const prevSubGroupBy = normalize(this.store.$effectiveView()?.table?.subGroupBy as string | null | undefined);
        const nextGroupBy = groupByEmitted ? normalize(table?.groupBy) : prevGroupBy;
        const nextSubGroupBy = groupByEmitted ? (nextGroupBy ? normalize(table?.subGroupBy) : null) : prevSubGroupBy;
        const groupByChanged = nextGroupBy !== prevGroupBy || nextSubGroupBy !== prevSubGroupBy;
        const prevFields = (this.store.$effectiveView()?.list?.fields ?? []).map(String).join('\0');
        const nextFields = list?.fields !== undefined ? list.fields.map(String).join('\0') : prevFields;
        const listFieldsChanged = list?.fields !== undefined && nextFields !== prevFields;
        const effectiveList = this.store.$effectiveView()?.list;
        const nestedDisplayChanged = (list?.showNestedItems !== undefined && list.showNestedItems !== effectiveList?.showNestedItems)
          || (list?.indentNestedItems !== undefined && list.indentNestedItems !== effectiveList?.indentNestedItems);

        // Contribution bag (file-scope, …). Only stamped when the panel includes `extensions`
        // (user touched a contribution control, or view already owned one).
        const nextExtensions = value.view?.extensions;
        const prevExtensions = this.store.$effectiveView()?.extensions;
        const extensionsChanged = JSON.stringify(nextExtensions ?? null) !== JSON.stringify(prevExtensions ?? null);

        this.store.ownViewDisplay({
          // Only stamped when the panel offered the switcher; otherwise the layout is not this
          // panel's to have an opinion about and an owned value would pin it behind the user's back.
          ...(value.displayChanged && value.view.display ? { display: value.view.display } : {}),
          ...(value.view.gantt !== undefined ? { gantt: value.view.gantt } : {}),
          ...(value.view.calendar?.view !== undefined ? { calendarView: value.view.calendar.view } : {}),
          showPreview: value.view.showPreview,
          pageSize: value.pageSize,
          sort: value.view.sort ?? null,
          // Persist groupBy (including null = None) so it is not sticky from localStorage, but only
          // when the panel actually offered the control — otherwise leave the owned value alone.
          ...(groupByEmitted ? { groupBy: nextGroupBy, subGroupBy: nextSubGroupBy } : {}),
          // List display properties + list options (only stamped when the panel emits them)
          ...(list?.fields !== undefined ? { listFields: list.fields.map(String) } : {}),
          ...(list?.titleLines !== undefined ? { titleLines: list.titleLines } : {}),
          ...(list?.showEmptyGroups !== undefined ? { showEmptyGroups: list.showEmptyGroups === true } : {}),
          ...(list?.showNestedItems !== undefined ? { showNestedItems: list.showNestedItems === true } : {}),
          ...(list?.indentNestedItems !== undefined ? { indentNestedItems: list.indentNestedItems === true } : {}),
          ...(nextExtensions !== undefined ? { extensions: nextExtensions } : {}),
        });

        if (nestedDisplayChanged) this.store.clearSelectedItems();

        // Group / pageSize / list fields / contribution scope change data shape or projection — reload.
        // (Display properties like createdAt/updatedAt need a refetch so values exist on the row.)
        // A layout change joins them: Kanban pages by swimlane and Table does not, so the same view
        // asks the server a different question depending on which one is on screen.
        if (!value.displayChanged && !pageSizeChanged && !groupByChanged && !listFieldsChanged && !extensionsChanged) {
            return;
        }

        this.store.clearSelectedItems();
        this.store.updateHeader({ page: 0, pageSize: value.pageSize });
        this.resetPaginator();
        // Force refetch even when page was already 0 (updateHeader alone may not change signals)
        queueMicrotask(() => this.store.refresh());
    }

    protected filterBar = viewChild<FilterBarComponent>('filterBar');

    protected onAddFilter(command: Command): void {

        this.filterBar()?.onAddFilter(command);
    }

    protected onFilterValuesChanged(values: Record<string, any>): void {

        this.store.clearSelectedItems();
        this.store.setFilterValues(values);
        this.store.updateHeader({ page: 0 });
        this.resetPaginator();
    }

    protected onSearch(event: any) {

        this.store.clearSelectedItems();
        this.store.updateHeader({ search: event, page: 0 });
        this.resetPaginator();
    }

    protected onPageChange(data: PaginatorState) {

        if (this.ignorePageChange) {
            this.ignorePageChange = false;
            return;
        }

        const next = data?.page ?? 0;
        const current = this.store.$page();

        // No-op echo (PrimeNG re-emits when totalRecords/first rebind).
        if (next === current) return;

        // Spurious page-0 only while total is unknown/loading — not a real "go to page 1" click.
        // (Blocking every next===0&&current>0 made page 1 unreachable.)
        const total = this.store.$total();
        if (next === 0 && current > 0 && (!total || this.store.$itemsLoading())) {
            return;
        }

        if (!this.store.$context()?.itemInteraction?.selectable?.retainAcrossPages) {
            this.store.clearSelectedItems();
        }
        this.store.updateHeader({ page: next });
    }

    /**
     * Countless paging is a plain step, not a jump: with no page count there is nothing to clamp
     * against, so the only safe moves are one back (never past 0) and one forward while the response
     * says another page exists.
     *
     * On a keyset board the step back also needs the cursor that page was fetched with. A cursor
     * cannot be reversed, so without the stored one there is no request to make and Back must refuse
     * rather than issue a position-less read that would silently answer with page 0's rows.
     */
    protected onCountlessPrevious(): void {

        const page = this.store.$page();
        if (page <= 0) return;
        // Page 0 is the one position that is legitimately absent — a walk starts without one.
        if (page > 1 && this.store.$context()?.paging === 'keyset' && this.store.$cursors()[page - 1] === undefined) return;

        if (!this.store.$context()?.itemInteraction?.selectable?.retainAcrossPages) {
            this.store.clearSelectedItems();
        }
        this.store.updateHeader({ page: page - 1 });
    }

    protected onCountlessNext(): void {

        if (!this.store.$hasMore()) return;

        if (!this.store.$context()?.itemInteraction?.selectable?.retainAcrossPages) {
            this.store.clearSelectedItems();
        }
        this.store.updateHeader({ page: this.store.$page() + 1 });
    }

    protected onRefreshNotice(): void {

        this.store.$context()?.header?.refreshNotice?.onRefresh();
        this.store.refresh();
    }

    private resetPaginator() {

        const paginator = this.paginator();
        if (!paginator) return;

        this.ignorePageChange = true;
        paginator.changePage(0);
    }
}
